import { useCallback, useContext, useEffect, useState } from 'react';

import { atom, useAtom } from 'jotai';
import { randomId } from 'ui/Hooks';

import { EditorContext } from './Editor.context';

type EditorHeaderNotification = {
  type?: 'success' | 'error';
  message?: string;
};

const editorNotificationHeaderAtom = atom<EditorHeaderNotification | undefined>(undefined);
const isEditorDirtyAtom = atom<boolean>(false);
const editorLocationAtom = atom<string>('');
const isEditorContentValidAtom = atom<boolean>(true);
const IsEditorValidating = atom(false);

const useEditor = () => {
  const {
    store,
    onDirty,
    isEditorLoading,
    isEditorReadOnly,
    editorLoadingHandler,
    key,
    setKey,
    headerRef,
    resetPreviewRef,
    saveEditorRef,
  } = useContext(EditorContext);
  const [editorNotification, setEditorNotification] = useAtom(editorNotificationHeaderAtom, {
    store,
  });
  const [isEditorDirty, setIsEditorDirty] = useAtom(isEditorDirtyAtom, { store });
  const [isEditorContentValid, setIsEditorContentValid] = useAtom(isEditorContentValidAtom, {
    store,
  });
  const [contentKey, setContentKey] = useState<undefined | string>();
  const [editorLocation, setEditorLocation] = useAtom(editorLocationAtom, { store });
  // Should the editor be checking the validation status of items
  const [isValidating, setIsValidating] = useAtom(IsEditorValidating, { store });
  const setResetPreviewRef = useCallback(
    (ref: () => void) => {
      if (!resetPreviewRef) return;
      if (!resetPreviewRef.current) {
        resetPreviewRef.current = ref;
      }
    },
    [resetPreviewRef]
  );

  const resetPreview = useCallback(() => {
    if (!resetPreviewRef) return;
    resetPreviewRef.current?.();
  }, [resetPreviewRef]);

  const saveEditor = useCallback(() => {
    if (!saveEditorRef) return;
    saveEditorRef.current?.();
  }, [saveEditorRef]);

  useEffect(() => {
    setContentKey(randomId());
  }, [key]);

  return {
    isEditorContentValid,
    setIsEditorContentValid,
    isEditorDirty,
    setIsEditorDirty,
    onDirty,
    isEditorLoading,
    isEditorReadOnly,
    editorLoadingHandler,
    contentKey,
    reloadContent: () => setKey?.((prev) => prev + 1),
    editorLocation,
    setEditorLocation,
    editorNotification,
    showResetNotification: useCallback(
      () =>
        setEditorNotification({
          type: 'success',
          message: 'Changes have been reset.',
        }),
      []
    ),

    showUnsavedChangesNotification: useCallback(
      () =>
        setEditorNotification({
          type: 'error',
          message:
            'You have unsaved changes. Click Save & Continue or Reset Changes before exiting.',
        }),
      []
    ),
    showPageErrorNotification: useCallback(
      () =>
        setEditorNotification({
          type: 'error',
          message: 'Please complete all required fields before proceeding',
        }),
      []
    ),

    showIncompleteFormNotification: useCallback((error: string) => {
      setEditorNotification({
        type: 'error',
        message: error,
      });
    }, []),
    isValidating,
    setIsValidating,
    clearNotification: useCallback(() => setEditorNotification(undefined), []),
    headerRef,
    setResetPreviewRef,
    resetPreview,
    saveEditor,
  };
};
export { useEditor };
